import {
  ERROR_CODE,
  IAppConfigurationVariables,
  LOG_LEVEL,
  Logger,
  getDaVinciAgentConfig,
} from '@amc-technology/davinci-api';
import {
  ILoggerConfiguration,
  defaultLoggerConfiguration,
} from '@amc-technology/davinci-api/dist/models/LoggerConfiguration';

import { ConfigurationService } from './configuration.service';
import { Injectable } from '@angular/core';
import { bind } from 'bind-decorator';
import { deepCopy } from '../utils';

@Injectable()
export class LoggerService {
  private _logger?: Logger;
  constructor(private readonly configService: ConfigurationService) {}
  localLog = localStorage.getItem('LocalLog') === 'true';

  get logger(): Logger {
    if (!this._logger) {
      throw new Error('Logger not initialized!');
    }
    return this._logger;
  }

  async initialize(): Promise<void> {
    const serverConfig = await this.configService.loadConfigurationData();
    try {
      // Get raw config. Framework does not perform data validation intentionally
      const rawConfig = await getDaVinciAgentConfig();

      // Perform data validation, revert to defaults if configs are invalids
      // or undefined

      let logLevel = parseInt(
        rawConfig.variables['Log Level'].toString(),
        10
      );

      logLevel = isNaN(logLevel)
        ? defaultLoggerConfiguration['Log Level']
        : logLevel;

      let maxLength = parseInt(
        (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          rawConfig['Console Logger'].variables as IAppConfigurationVariables
        )['Max Length'].toString(),
        10
      );

      maxLength = isNaN(maxLength)
        ? defaultLoggerConfiguration['Console Logger']['Max Length']
        : maxLength;

      let maxFailedLogBufferSize = parseInt(
        rawConfig.variables['Max Failed Log Buffer Size']?.toString(),
        10
      );

      maxFailedLogBufferSize = isNaN(maxFailedLogBufferSize)
        ? defaultLoggerConfiguration['Max Failed Log Buffer Size']!
        : maxFailedLogBufferSize;

      let maxLogBufferSize = parseInt(
        rawConfig.variables['Max Log Buffer Size']?.toString(),
        10
      );

      maxLogBufferSize = isNaN(maxLogBufferSize)
        ? defaultLoggerConfiguration['Max Log Buffer Size']!
        : maxLogBufferSize;

      let maxLogRetention = parseInt(
        rawConfig.variables['Max Log Retention']?.toString(),
        10
      );

      maxLogRetention = isNaN(maxLogRetention)
        ? defaultLoggerConfiguration['Max Log Retention']!
        : maxLogRetention;

      const loggerConfig: ILoggerConfiguration = {
        'Log Level': logLevel,
        'Logger Type':
          rawConfig.variables['Logger Type'].toString() ||
          defaultLoggerConfiguration['Logger Type'],
        'Premise Logger URL':
          rawConfig.variables['Premise Logger URL'].toString() ||
          defaultLoggerConfiguration['Premise Logger URL'],
        'Max Failed Log Buffer Size': maxFailedLogBufferSize,
        'Max Log Buffer Size': maxLogBufferSize,
        'Max Log Retention': maxLogRetention,
        'Console Logger': {
          'Max Length': maxLength,
        },
      };
      this._logger = new Logger(
        'SalesforceBYOT',
        false,
        serverConfig.loggerApiUrl
      );

      this._logger.setConfiguration(loggerConfig);
    } catch {
      this._logger = new Logger(
        'SalesforceBYOT',
        false,
        serverConfig.loggerApiUrl
      );
      this._logger.logCritical(
        'loggerService.initialize(): Error creating logger!'
      );
    }
  }

  /**
   * Logs a message to the loggerApi
   *
   * @param {LOG_LEVEL} logLevel - The level of the log
   * @param {string} fName - Name of the function logging the message
   * @param {string} message - Message to log
   * @param {*} [object] - Optional object to log
   * @param {ERROR_CODE} [errorCode] - Optional error code
   * @param {Date} [localTime] - Optional local time
   * @memberof LoggerService
   */
  @bind
  log(
    logLevel: LOG_LEVEL,
    fName: string,
    message: string,
    object?: any,
    errorCode?: ERROR_CODE,
    localTime?: Date
  ): void {
    const functionName = 'log';
    try {

      if (object) {
        object = deepCopy(object);
      }

      if (this.localLog) {
        this.localLogMessage(logLevel, 'BYOT | ' + fName, message, object);
      }
      switch (logLevel) {
      case LOG_LEVEL.Loop:
        this._logger?.logLoop(`${fName} | ${message}` + (object ? ` | ${JSON.stringify(object, this.stringifyReplacer(), 2)}` : ''), errorCode, localTime);
        break;
      case LOG_LEVEL.Trace:
        this._logger?.logTrace(`${fName} | ${message}` + (object ? ` | ${JSON.stringify(object, this.stringifyReplacer(), 2)}` : ''), errorCode, localTime );
        break;
      case LOG_LEVEL.Information:
        this._logger?.logInformation(`${fName} | ${message}` + (object ? ` | ${JSON.stringify(object, this.stringifyReplacer(), 2)}` : ''), errorCode, localTime);
        break;
      case LOG_LEVEL.Warning:
        this._logger?.logWarning( `${fName} | ${message}` + (object ? ` | ${JSON.stringify(object, this.stringifyReplacer(), 2)}` : ''), errorCode, localTime);
        break;
      case LOG_LEVEL.Error:
        this._logger?.logError(`${fName} | ${message}` + (object ? ` | ${JSON.stringify(object, this.stringifyReplacer(), 2)}` : ''), errorCode, localTime);
        break;
      case LOG_LEVEL.Critical:
        this._logger?.logCritical(`${fName} | ${message}` + (object ? ` | ${JSON.stringify(object, this.stringifyReplacer(), 2)}` : ''), errorCode, localTime);
        break;
      case LOG_LEVEL.Debug:
        this._logger?.logDebug(`${fName} | ${message}` + (object ? ` | ${JSON.stringify(object, this.stringifyReplacer(), 2)}` : ''), errorCode, localTime);
        break;
      default:
        this._logger?.logInformation(`${fName} | ${message}` + (object ? ` | ${JSON.stringify(object, this.stringifyReplacer(), 2)}` : ''), errorCode, localTime);
        break;
      }
    } catch (e) {
      this._logger?.logError(`${functionName} - Failed to log ${logLevel} from ${fName}. Error: ${JSON.stringify(e, Object.getOwnPropertyNames(e))}`);
      console.log(`${functionName} - Failed to log ${logLevel} from ${fName}. Error: ${JSON.stringify(e, Object.getOwnPropertyNames(e))}`);
    }
  }

  /**
   * This function logs a message to the console that is color coded based off of its LOG_LEVEL
   *
   * @private
   * @param {LOG_LEVEL} logLevel
   * @param {string} fName
   * @param {string} message
   * @param {*} [object]
   * @param {ERROR_CODE} [errorCode]
   * @param {Date} [localTime]
   * @memberof LoggerService
   */
  private localLogMessage(
    logLevel: LOG_LEVEL,
    fName: string,
    message: string,
    object?: any
  ): void {
    const functionName = 'localLogMessage';
    try {
      let backgroundColor = '#003300';
      let textColor = '#00ff00';

      switch (logLevel) {
      case LOG_LEVEL.Loop:
        backgroundColor = '#000033';
        textColor = '#00ff77';
        break;
      case LOG_LEVEL.Trace:
        backgroundColor = '#000033';
        textColor = '#0077ff';
        break;
      case LOG_LEVEL.Information:
        backgroundColor = '#330033';
        textColor = '#ff00ff';
        break;
      case LOG_LEVEL.Debug:
        backgroundColor = '#003300';
        textColor = '#00ff00';
        break;
      case LOG_LEVEL.Warning:
        backgroundColor = '#333300';
        textColor = '#ffff00';
        break;
      case LOG_LEVEL.Error:
        backgroundColor = '#330000';
        textColor = '#ff0000';
        break;
      case LOG_LEVEL.Critical:
        backgroundColor = '#333300';
        textColor = '#ff6600';
        break;
      default:
        backgroundColor = '#003300';
        textColor = '#00ff00';
        break;
      }
      const fontWeight = 'bold';
      if (object != null && typeof object === 'object') {
        if (logLevel === LOG_LEVEL.Loop || logLevel === LOG_LEVEL.Trace) {
          console.groupCollapsed(
            `%c${fName} | ${LOG_LEVEL[logLevel]} | ${message}`,
            `background: ${backgroundColor}; color: ${textColor}; font-weight: ${fontWeight};`
          );
        } else {
          console.group(
            `%c${fName} | ${LOG_LEVEL[logLevel]} | ${message}`,
            `background: ${backgroundColor}; color: ${textColor}; font-weight: ${fontWeight};`
          );
        }
        const printableObject = {
          functionName: fName,
          message: message,
          object: object,
        };
        console.log(printableObject);
        console.groupEnd();
      } else {
        console.log(
          `%c${fName} | ${LOG_LEVEL[logLevel]} | ${message}${
            object != null ? ` | ${object}` : ''
          }`,
          `background: ${backgroundColor}; color: ${textColor}; font-weight: ${fontWeight};`
        );
      }
    } catch (e) {
      console.log(
        `${functionName} - Failed to log ${logLevel} from ${fName}. Error: ${JSON.stringify(
          e,
          Object.getOwnPropertyNames(e)
        )}`
      );
    }
  }

  private stringifyReplacer(): (key: string, value: any) => any {
    const ancestors: any[] = [];
    return (key: string, value: any) => {
      if (typeof value !== 'object' || value === null) {
        if (value instanceof Set) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return [...value];
        }
        if (key === 'promise') {
          return {};
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return value;
      } else {
        while (
          ancestors.length > 0 &&
          ancestors[ancestors.length - 1] !== this
        ) {
          ancestors.pop();
        }
        if (ancestors.includes(value)) {
          return '[Circular]';
        }
        ancestors.push(value);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return value;
      }
    };
  }
}
